import { PanelDefaultRoute } from "@src/router/routes";
import { UserContext } from "@src/utility/context/user";
import { toastify } from "@src/utility/helpers/toast";
import { getItemFromStore, setItemToStore } from "@src/utility/helpers/utils";
import useAxios from "@src/utility/hooks/use-axios";
import usePanelAbility from "@src/utility/hooks/use-panel-ability";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { COOLDOWN_SECONDS, DIGITS } from "../misc/constants";
import useSearchParams from "@src/utility/hooks/use-search-params";

export const useTwoFactorVerification = () => {
    const [values, setValues] = useState((Array(DIGITS)).fill(""));
    const inputsRef = useRef([]);
    const [cooldown, setCooldown] = useState(0);
    const timerRef = useRef(null);
    const { handlePanelAbility } = usePanelAbility();
    const { handleUserData } = useContext(UserContext);
    const { axiosInstance:verifyAxiosInstance, loading: verifyLoading } = useAxios({ base: "panel", alertTypeOnSuccess: "toast" });
    
    const { axiosInstance: resendAxiosInstance, loading: resendLoading } = useAxios({ base: "panel", alertTypeOnSuccess: "toast", showAlert: false });
    const navigate = useNavigate();
    const { getSearchParams } = useSearchParams();
    const verification_token = getSearchParams("verification_token");
    
    const getRemainingCooldown = () => {
        const expiry = Number(getItemFromStore("resend_expiry"));
        if (!expiry) return 0;
        const remaining = Math.floor((expiry - Date.now()) / 1000);
        return remaining > 0 ? remaining : 0;
    };

    const startCooldown = (seconds) => {
        const expiryTime = Date.now() + (seconds * 1000);
        setItemToStore("resend_expiry", expiryTime);
        setCooldown(seconds);

        if (timerRef.current) window.clearInterval(timerRef.current);
        timerRef.current = window.setInterval(() => {
            setCooldown((c) => {
                if (c <= 1) {
                    window.clearInterval(timerRef.current);
                    setItemToStore("resend_expiry", null);
                    return 0;
                }
                return c - 1;
            });
        }, 1000);
    };

    useEffect(() => {
        const remaining = getRemainingCooldown();
        if (remaining > 0) {
            startCooldown(remaining);
        }

        const checkValidRedirection = async () => {
            try {
                await resendAxiosInstance.get(
                    "/user/validate-token",
                    { headers: { Authorization: `Bearer ${verification_token}` } }
                );
            } catch (error) {
                navigate("/login");
            }
        };
        checkValidRedirection();

        return () => {
            if (timerRef.current) window.clearInterval(timerRef.current);
        };
    }, []);

    const updateValueAt = (index, val) => {
        setValues((prev) => {
            const copy = [...prev];
            copy[index] = val;
            return copy;
        });
    };

    const handleChange = (index, raw) => {
        const char = raw.replace(/\D/g, "");
        if (!char) {
            updateValueAt(index, "");
            return;
        }
        updateValueAt(index, char[0]);

        const next = index + 1;
        if (next < DIGITS) {
            inputsRef.current[next]?.focus();
            inputsRef.current[next]?.select();
        }
    };
    
    const handleLogin = user => {
        handlePanelAbility({ userInfo: { user } });
        navigate(PanelDefaultRoute);
    };
    const submitCode = async () => {
        const code = values.join("");
        
        if (code.length !== DIGITS) {
            toastify({
                title: "OTP",
                desc : "Please enter the 6 digit otp.",
                type : "warning"
            });
            return;
        }
        const { data } = await verifyAxiosInstance.post(
            "/user/two-fa-verification",
            { code },
            { headers: { Authorization: `Bearer ${verification_token}` } }
        );
        setItemToStore("token", data.token);
        setItemToStore("access_email", data.email_permission);
        setItemToStore("access_calendar", data.calendar_permission);
        handleUserData({ user: data.user });
        handleLogin(data.user);

    };

    const handleKeyDown = (e, idx) => {
        const key = e.key;

        switch (key) {
        case "Backspace":
            if (!values[idx]) {
                const prev = Math.max(0, idx - 1);
                inputsRef.current[prev]?.focus();
                updateValueAt(prev, "");
            } else {
                updateValueAt(idx, "");
            }
            break;

        case "ArrowLeft":
            inputsRef.current[Math.max(0, idx - 1)]?.focus();
            break;

        case "ArrowRight":
            inputsRef.current[Math.min(DIGITS - 1, idx + 1)]?.focus();
            break;

        case "Enter":
            submitCode();
            break;

        default:
            break;
        }
    };

    // support paste of full code
    const handlePaste = (e) => {
        const pasted = e.clipboardData.getData("Text").replace(/\s/g, "").replace(/\D/g, "");
        if (!pasted) return;
        const digits = pasted.split("").slice(0, DIGITS);
        setValues((prev) => {
            const copy = [...prev];
            for (let i = 0; i < DIGITS; i++) {
                copy[i] = digits[i] ?? "";
            }
            return copy;
        });
        // focus next empty or last
        const firstEmpty = digits.length < DIGITS ? digits.length : DIGITS - 1;
        inputsRef.current[firstEmpty]?.focus();
    };

    const handleResend = async () => {
        startCooldown(COOLDOWN_SECONDS);
        const { message } = await resendAxiosInstance.get(
            "/user/regenerate-otp",
            { headers: { Authorization: `Bearer ${verification_token}` } }
        );
        toastify({
            title: "OTP",
            desc : message,
            type : "success"
        });
        setValues((Array(DIGITS)).fill(""));
      
    };

    return {
        handleResend,
        handlePaste,
        handleKeyDown,
        submitCode,
        handleChange,
        values,
        inputsRef,
        cooldown,
        resendLoading,
        verifyLoading
    };
};